import { FocusEvent, InputHTMLAttributes, ReactElement, useId, useRef } from 'react'
import { Form } from 'radix-ui'

type ValidityMatchKey =
  | 'badInput'
  | 'patternMismatch'
  | 'rangeOverflow'
  | 'rangeUnderflow'
  | 'stepMismatch'
  | 'tooLong'
  | 'tooShort'
  | 'typeMismatch'
  | 'valueMissing'

type Messages = Partial<Record<ValidityMatchKey, string>>

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  onBlur: (e: FocusEvent<HTMLInputElement>) => void
}

type AccessibleFormFieldProps = {
  name: string
  label: string
  className?: string
  labelClassName?: string
  messageClassName?: string
  inputClassName?: string
  messages: Messages
  announce: (msg: string) => void
  children: (inputProps: InputProps) => ReactElement
  required?: boolean
}

const isInvalid = (validity?: ValidityState) => {
  if (!validity) {
    return false
  }
  return !validity.valid
}

const getErrorMessage = (validity: ValidityState | undefined, messages: Messages) => {
  if (!validity) {
    return ''
  }
  for (const [key, msg] of Object.entries(messages)) {
    if (validity[key as keyof ValidityState]) {
      return msg
    }
  }
  return ''
}

const getActiveErrorId = (
  validity: ValidityState | undefined,
  messages: Messages,
  errorIdPrefix: string,
) => {
  if (!validity) {
    return undefined
  }
  for (const key of Object.keys(messages)) {
    if (validity[key as keyof ValidityState]) {
      return `${errorIdPrefix}-${key}`
    }
  }
  return undefined
}

export function AccessibleFormField({
  name,
  label,
  className,
  labelClassName,
  messageClassName,
  inputClassName,
  messages,
  announce,
  children,
  required,
}: AccessibleFormFieldProps) {
  const id = useId()
  const errorIdPrefix = `${id}-error`
  const lastMessage = useRef('')

  return (
    <Form.Field name={name} className={className}>
      <Form.Label htmlFor={id} className={labelClassName}>
        {label}
      </Form.Label>

      <Form.ValidityState>
        {(validity) => {
          const invalid = isInvalid(validity)
          const msg = getErrorMessage(validity, messages)

          if (invalid && msg && msg !== lastMessage.current) {
            lastMessage.current = msg
            setTimeout(() => announce(msg), 0)
          }

          if (!invalid) {
            lastMessage.current = ''
          }

          return null
        }}
      </Form.ValidityState>

      {Object.entries(messages).map(([matchKey, text]) => (
        <Form.Message
          key={matchKey}
          id={`${errorIdPrefix}-${matchKey}`}
          className={messageClassName}
          match={matchKey as ValidityMatchKey}>
          {text}
        </Form.Message>
      ))}

      <Form.ValidityState>
        {(validity) => {
          const invalid = isInvalid(validity)
          const activeErrorId = invalid
            ? getActiveErrorId(validity, messages, errorIdPrefix)
            : undefined

          return (
            <Form.Control asChild>
              {children({
                id,
                name,
                required,
                className: inputClassName,
                'aria-invalid': invalid,
                'aria-describedby': activeErrorId,
                onBlur: (e: FocusEvent<HTMLInputElement>) => {
                  const inputValidity = e.currentTarget.validity
                  if (isInvalid(inputValidity)) {
                    const msg = getErrorMessage(inputValidity, messages)
                    if (msg) {
                      announce(msg)
                    }
                  }
                },
              })}
            </Form.Control>
          )
        }}
      </Form.ValidityState>
    </Form.Field>
  )
}
